import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { sortByLabel } from './SelectInputBase';

interface ChipMultiSelectProps<T extends string | number = string> {
  label: string;
  options: Array<{ label: string; value: T }>;
  value: T[];
  onChange: (value: T[]) => void;
  disableAlphabeticalSort?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  showCheckboxes?: boolean;
  style?: React.CSSProperties;
}

function ChipMultiSelect<T extends string | number = string>({
  label,
  options,
  value,
  onChange,
  disableAlphabeticalSort = false,
  variant = 'standard',
  fullWidth = false,
  size,
  showCheckboxes = false,
  style,
}: ChipMultiSelectProps<T>) {
  const displayOptions = disableAlphabeticalSort ? options : sortByLabel(options);

  const handleChange = (event: SelectChangeEvent<T[]>) => {
    const raw = event.target.value;
    onChange(typeof raw === 'string' ? ([raw] as T[]) : (raw as T[]));
  };

  return (
    <FormControl variant={variant} fullWidth={fullWidth} size={size} style={style}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        label={variant === 'outlined' ? label : undefined}
        onChange={handleChange}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(selected as T[]).map((val) => {
              const option = displayOptions.find((o) => o.value === val);
              return <Chip key={String(val)} label={option?.label ?? String(val)} size="small" />;
            })}
          </Box>
        )}
      >
        {displayOptions.map((opt) => (
          <MenuItem key={String(opt.value)} value={opt.value as any}>
            {showCheckboxes && <Checkbox size="small" checked={value.includes(opt.value)} />}
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default ChipMultiSelect;
